import React from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { getType } from 'typesafe-actions';
import {
  Box, Checkbox, Card, CardContent, IconButton,
  Grid, InputAdornment, TextField, Typography
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import AddBoxRoundedIcon from '@material-ui/icons/AddBoxRounded';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { ContactListItemDto } from '../contacts.dtos';
import ContactListCard from './contactListCard';
import { contactShouldRefresh } from '../contacts.actions';
import { getContactsApi } from '../contacts.api';
import { isReadOnly } from '../../../common/dtos/auth';
import { Checkbox1, EndButton, LargeIconButton } from '../../../common/styles/styles';
import { FeatureTypeEnum } from '../../../common/enums/featureTypeEnum';
import {
  parseContactsListQuery,
  buildContactsListSearch,
} from '../contactsListQuery';

const ContactList: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [listState, setListState] = React.useState<{
    items: ContactListItemDto[];
    totalCount: number;
  }>({ items: [], totalCount: 0 });

  const parsed = React.useMemo(
    () => parseContactsListQuery(location.search),
    [location.search]
  );

  React.useEffect(() => {
    let cancelled = false;
    const q = parseContactsListQuery(location.search);
    (async () => {
      try {
        const { items, totalCount } = await getContactsApi(q);
        if (!cancelled) setListState({ items, totalCount });
      } catch {
        if (!cancelled) setListState({ items: [], totalCount: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const contactsList = listState.items;
  const resultCount = listState.totalCount;

  const onAddContact = () => {
    const s = isReadOnly(FeatureTypeEnum.Contacts);
    if (s === false)
      dispatch({ type: getType(contactShouldRefresh), payload: '' });
    window.location.assign('contact/new');
  };

  const onCompaniesImport = () => {
    history.push('/bulk-import/contacts');
  };

  const navigateWithQuery = (next: typeof parsed) => {
    history.replace({ pathname: location.pathname, search: buildContactsListSearch(next) });
  };

  const handleViewActiveOnlyToggle = () => {
    navigateWithQuery({
      ...parsed,
      showInactive: !parsed.showInactive,
      page: 1,
    });
  };

  const handleCancelSearch = () => {
    navigateWithQuery({
      ...parsed,
      search: '',
      page: 1,
    });
  };

  const pageSize = parsed.pageSize;
  const pageCount = Math.max(1, Math.ceil(resultCount / pageSize));

  return <>
    <Box p={2}>
      <Grid container data-cy="contact_list_Container">
        <Grid item xs={11}>
          <Typography variant="subtitle1" display="inline">Contacts</Typography>
          <Typography variant="subtitle1" color="textSecondary" display="inline">{" "} ({resultCount})</Typography>
        </Grid>
        <Grid item xs={1} hidden={isReadOnly(FeatureTypeEnum.Contacts)} >
          <LargeIconButton title="Add Contact" hidden={isReadOnly(FeatureTypeEnum.Contacts)} color="secondary" onClick={onAddContact} data-cy="contact_add_button">
            <AddBoxRoundedIcon />
          </LargeIconButton>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <Formik initialValues={{ search: parsed.search }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={Yup.object({
                search: Yup.string().max(200, 'Must be 200 characters or less')
              })}
              onSubmit={(values) => {
                navigateWithQuery({
                  ...parsed,
                  search: values.search ?? '',
                  page: 1,
                });
              }}
              enableReinitialize={true}
            >
              {(props) => {
                const { values, touched, errors, handleChange, handleSubmit, handleBlur } = props;
                return (
                  <form onSubmit={handleSubmit}>
                    <TextField
                      id="search"
                      size="small"
                      margin="none"
                      value={values.search}
                      label="Search"
                      name="search"
                      fullWidth
                      onChange={handleChange}
                      onBlur={(e) => { handleBlur(e); handleSubmit(); }}
                      error={Boolean(errors.search)}
                      helperText={(errors.search && touched.search) && errors.search}
                      data-cy="contactsListSearch"
                      InputProps={{
                        startAdornment: (<InputAdornment position="start">
                          <IconButton
                            aria-label="Search Contacts"
                            onClick={() => handleSubmit()}
                            edge="start"
                          >
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>),
                        endAdornment: (values.search.length === 0 ? <></> : <InputAdornment position="end">
                          <IconButton
                            aria-label="Clear Search"
                            onClick={() => handleCancelSearch()}
                            edge="start"
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>)
                      }}
                    />
                  </form>);
              }}
            </Formik>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Checkbox1 onChange={handleViewActiveOnlyToggle} value={parsed.showInactive} checked={parsed.showInactive}
              control={<Checkbox size="small" data-cy="pageTemplate_template_3ViewActiveOnly" />}
              label={<Typography variant="subtitle2">Show inactive</Typography>} />
          </Grid>
          <Grid item xs={12} sm={4} hidden={isReadOnly(FeatureTypeEnum.Contacts)}>
            <EndButton hidden={isReadOnly(FeatureTypeEnum.Contacts)} title="Bulk Import" type="button" variant="contained" color="primary"
              onClick={onCompaniesImport} disabled={isReadOnly(FeatureTypeEnum.Contacts)} data-cy="contactsListImport">Bulk Import</EndButton>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {contactsList
            .map((o: ContactListItemDto, index: number) => (
              <ContactListCard key={(o.contactId ?? '') + '-' + index} contact={o} />
            ))}
        </Grid>

        <Grid item xs={12}>&nbsp;</Grid>
        <Grid item xs={12}>
          <Pagination
            hidden={resultCount === 0}
            count={pageCount}
            variant="outlined"
            color="primary"
            size="small"
            page={parsed.page}
            onChange={(_e, p) => {
              navigateWithQuery({ ...parsed, page: p });
            }} />
        </Grid>

        {contactsList.length === 0 &&
          <Grid item xs={12}>
            <Card>
              <CardContent>
              <Grid container justifyContent="center" alignItems="center">
                  <Typography variant="subtitle1" color="secondary">No records found{parsed.search.length === 0 ? '.' : ' matching ' + parsed.search}</Typography>
                </Grid>
              </CardContent>
            </Card>
            <br />
          </Grid>
        }
      </Grid>
    </Box>
  </>;
};

export default ContactList;
